import { AxiosInstance, CancelToken } from 'axios';

import * as common from '../../../../../../typings/api/skymap/rest/v1/.common';
import * as types from '../../../../../../typings/api/skymap/rest/v1/project';
import { toUrlSearchParams } from './utils';

export class ProjectService implements types.Handlers {
  constructor(private axios: AxiosInstance) {}

  async removeDroneHarmonyIntegration(
    params: types.RemoveDroneHarmonyIntegrationParameters,
  ): Promise<void> {
    await this.axios.delete<void>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony`,
    );
  }

  async createDroneHarmonyFlightPlan(
    params: types.CreateDroneHarmonyFlightPlanParameters,
  ): Promise<types.CreateDroneHarmonyFlightPlanResponse> {
    const { data } = await this.axios.post<types.CreateDroneHarmonyFlightPlanResponse>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony/flight-plans`,
      params.body,
    );
    return data;
  }

  async fetchDroneHarmonyFlightPlans(
    params: types.FetchDroneHarmonyFlightPlansParameters,
  ): Promise<types.FetchDroneHarmonyFlightPlanResponse> {
    const { data } = await this.axios.get<types.FetchDroneHarmonyFlightPlanResponse>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony/flight-plans`,
    );
    return data;
  }

  async deleteDroneHarmonyFlightPlan(
    params: types.DeleteDroneHarmonyFlightPlanParameters,
  ): Promise<void> {
    await this.axios.delete<void>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony/flight-plans/${params.path.flightPlanId}`,
    );
  }

  onDroneHarmonyNotification(params: types.OnDroneHarmonyNotificationParameters): Promise<void> {
    throw new Error('Method not implemented.');
  }

  async getDroneHarmonyIntegration(
    params: types.GetDroneHarmonyIntegrationParameters,
  ): Promise<types.DroneHarmonyIntegrationResponse> {
    const { data } = await this.axios.get<types.DroneHarmonyIntegrationResponse>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony`,
    );
    return data;
  }

  async createDroneHarmonyIntegration(
    params: types.CreateDroneHarmonyIntegrationParameters,
  ): Promise<types.DroneHarmonyIntegrationResponse> {
    const { data } = await this.axios.post<types.DroneHarmonyIntegrationResponse>(
      `/v1/projects/${params.path.projectId}/integrations/drone-harmony`,
    );
    return data;
  }

  async deleteAllAttachedDongles(params: types.DeleteAllAttachedDonglesParameters): Promise<void> {
    const projectId: string = params.path.projectId;
    await this.axios.delete(`v1/projects/${projectId}/dongles`);
  }

  async getAttachedDonglesList(
    params: types.GetAttachedDonglesListParameters,
  ): Promise<common.FetchSkyMapConnectDongleRegistrationsResponse> {
    const projectId: string = params.path.projectId;
    const { data } = await this.axios.get<common.FetchSkyMapConnectDongleRegistrationsResponse>(
      `v1/projects/${projectId}/dongles`,
    );
    return data;
  }

  async getArchiveJobs(
    params: types.GetArchiveJobsParameters,
  ): Promise<types.GetProjectArchiveJobsResponse> {
    const { data } = await this.axios.get<types.GetProjectArchiveJobsResponse>(
      `/v1/projects/${params.path.projectId}/archive-jobs`,
      {
        params: new URLSearchParams([
          ...toUrlSearchParams('include', params.query.include),
          ...toUrlSearchParams('orderBy', params.query.orderBy),
        ]),
      },
    );
    return data;
  }
  async getRestoreJobs(
    params: types.GetRestoreJobsParameters,
  ): Promise<types.GetProjectRestoreJobsResponse> {
    const { data } = await this.axios.get<types.GetProjectRestoreJobsResponse>(
      `/v1/projects/${params.path.projectId}/restore-jobs`,
      {
        params: new URLSearchParams([
          ...toUrlSearchParams('include', params.query.include),
          ...toUrlSearchParams('orderBy', params.query.orderBy),
        ]),
      },
    );
    return data;
  }

  async addArchiveJob(params: types.AddArchiveJobParameters): Promise<void> {
    await this.axios.post<common.FolderModelResponse>(
      `/v1/projects/${params.path.projectId}/archive-jobs`,
      params.body,
    );
  }

  async addRestoreJob(params: types.AddRestoreJobParameters): Promise<void> {
    await this.axios.post<common.FolderModelResponse>(
      `/v1/projects/${params.path.projectId}/restore-jobs`,
    );
  }

  async deleteArchiveJob(params: types.DeleteArchiveJobParameters): Promise<void> {
    await this.axios.delete<common.FolderModelResponse>(
      `/v1/projects/${params.path.projectId}/archive-jobs/${params.path.archiveJobId}`,
    );
  }

  async patchProject(params: types.PatchProjectParameters): Promise<void> {
    await this.axios.patch<common.FileModelResponse>(
      `/v1/projects/${params.path.projectId}`,
      params.body,
    );
  }

  async getRootFolders(
    params: types.GetRootFoldersParameters,
    cancelToken?: CancelToken,
  ): Promise<common.GetFoldersResponse> {
    const { data } = await this.axios.get<common.GetFoldersResponse>(
      `/v1/projects/${params.path.projectId}/folders`,
      {
        params: new URLSearchParams([
          ...(params.query.includeSubFolders ? [['includeSubFolders', 'true']] : []),
          ...(params.query.include ?? []).map((x) => ['include', x]),
          ...(params.query.orderBy ?? []).map((x) => ['orderBy', x]),
        ]),
        cancelToken: cancelToken,
      },
    );
    return data;
  }

  async createRootFolder(
    params: types.CreateRootFolderParameters,
  ): Promise<common.FolderModelResponse> {
    const { data } = await this.axios.post<common.FolderModelResponse>(
      `/v1/projects/${params.path.projectId}/folders`,
      params.body,
    );
    return data;
  }

  async createRootFolderFile(
    params: types.CreateRootFolderFileParameters,
  ): Promise<common.CreateFileResponse> {
    const { data } = await this.axios.post<common.CreateFileResponse>(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `/v1/projects/${params.path.projectId}/files`,
      params.body,
    );
    return data;
  }

  async getRootFolderFiles(
    params: types.GetRootFolderFilesParameters,
    cancelToken?: CancelToken,
  ): Promise<common.GetFilesResponse> {
    const { data } = await this.axios.get<common.GetFilesResponse>(
      `/v1/projects/${params.path.projectId}/files`,
      {
        params: new URLSearchParams([
          ...(params.query.include ?? []).map((x) => ['include', x]),
          ...(params.query.orderBy ?? []).map((x) => ['orderBy', x]),
        ]),
        cancelToken: cancelToken,
      },
    );
    return data;
  }

  async getTiledModels(
    params: types.GetTiledModelsParameters,
  ): Promise<common.GetTiledModelListResponse> {
    const { data } = await this.axios.get<common.GetTiledModelListResponse>(
      `/v1/projects/${params.path.projectId}/tiled-models`,
    );
    return data;
  }

  async getCoordinateSystem(
    params: types.GetCoordinateSystemParameters,
  ): Promise<common.CoordinateSystemResponse> {
    const { data } = await this.axios.get<common.CoordinateSystemResponse>(
      `/v1/projects/${params.path.projectId}/coordinate-system`,
    );
    return data;
  }

  async putCoordinateSystem(
    params: types.PutCoordinateSystemParameters,
  ): Promise<common.CoordinateSystemResponse> {
    const { data } = await this.axios.put<common.CoordinateSystemResponse>(
      `/v1/projects/${params.path.projectId}/coordinate-system`,
      params.body,
    );
    return data;
  }

  async getGeoid(params: types.GetGeoidParameters): Promise<common.GetGeoidResponse> {
    const { data } = await this.axios.get<common.GetGeoidResponse>(
      `/v1/projects/${params.path.projectId}/geoid`,
    );
    return data;
  }

  async putGeoid(params: types.PutGeoidParameters): Promise<common.GetGeoidResponse> {
    const { data } = await this.axios.put<common.GetGeoidResponse>(
      `/v1/projects/${params.path.projectId}/geoid`,
      params.body,
    );
    return data;
  }

  async getRealProperties(
    params: types.GetRealPropertiesParameters,
  ): Promise<types.GetRealPropertiesResponse> {
    const { data } = await this.axios.get<types.GetRealPropertiesResponse>(
      `/v1/projects/real-properties`,
      {
        params: params.query,
      },
    );
    return data;
  }

  async getProjectRealProperties(
    params: types.GetProjectRealPropertiesParameters,
  ): Promise<types.GetProjectRealPropertiesResponse> {
    const { data } = await this.axios.get<types.GetProjectRealPropertiesResponse>(
      `/v1/projects/${params.path.projectId}/real-properties`,
    );
    return data;
  }

  async createProjectRealProperty(
    params: types.CreateProjectRealPropertyParameters,
  ): Promise<types.CreateProjectRealPropertyResponse> {
    const { data } = await this.axios.post<types.CreateProjectRealPropertyResponse>(
      `/v1/projects/${params.path.projectId}/real-properties/${params.path.externalId}`,
    );
    return data;
  }

  async getProjectGeodata(
    params: types.GetProjectGeodataParameters,
  ): Promise<types.FetchProjectGeodataListResponse> {
    const { data } = await this.axios.get<types.FetchProjectGeodataListResponse>(
      `/v1/projects/${params.path.projectId}/geodata`,
    );
    return data;
  }
}
